import { hasEmoji } from '@/shared/utils/emoji';
import { PHONE_REGEX, normalizePhone, toPhoneInputValue } from '@/shared/utils/phone';

import type {
  Patient,
  PatientGender,
  PatientGuardianRelationship,
  PatientMedication,
  UpdatePatientInput,
} from '../types/patient.types';
import {
  GUARDIAN_NAME_MAX,
  GUARDIAN_NAME_MIN,
  MEDICATION_MAX,
  MRN_REGEX,
  PATIENT_NAME_REGEX,
  RESIDENCE_DETAIL_MAX,
  RESIDENCE_MAX,
  initialMedicationRows,
  medicationRows,
  toDateInputValue,
  todayInKst,
} from './patient-field-rules';

// SHCT-910 · SHCT-981 — PRD 02 v1.37 §3.6 전체 편집 모드. The panel holds ONE draft of every editable
// field; [저장] validates the lot and sends a single PUT carrying only what changed (an absent key is
// "leave alone" on the wire). Kept as pure functions so the diff and the rules can be read — and
// tested — apart from the 480px of markup that renders them.

/**
 * 주진단 + 부진단 as the operator left them. `null` on the draft means "not touched": the detail response
 * names the diagnosis but carries no ids, so the ids only exist once the master lists resolve, and an
 * untouched pair must never be sent just because it could not be resolved.
 *
 * The NAME and CODE ride along with the ids so the diff can compare against what the detail response
 * actually carries — no second lookup at save time.
 */
export interface DraftDiagnosis {
  groupId: number | null;
  groupName: string | null;
  codeId: number | null;
  code: string | null;
}

/** One 담당 의료진 entry. `name` is null for a staff account that was deleted but is still assigned. */
export interface DraftStaff {
  id: number;
  name: string | null;
}

export interface PatientDraft {
  name: string;
  gender: PatientGender | '';
  birthDate: string;
  regNo: string;
  diagnosis: DraftDiagnosis | null;
  medication: PatientMedication[];
  dischargeDate: string;
  staff: DraftStaff[];
  phone: string;
  residence: string;
  residenceDetail: string;
  guardianName: string;
  guardianRelationship: PatientGuardianRelationship | '';
  guardianPhone: string;
}

export type DraftField = keyof PatientDraft;
export type DraftErrors = Partial<Record<DraftField, string>>;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The draft an edit session opens with — every input holds the stored, unmasked value (§4.3). */
export function draftFromPatient(patient: Patient): PatientDraft {
  return {
    name: patient.name ?? '',
    gender: patient.gender ?? '',
    birthDate: toDateInputValue(patient.birthDate),
    regNo: patient.regNo ?? '',
    diagnosis: null,
    medication: initialMedicationRows(patient),
    dischargeDate: toDateInputValue(patient.dischargeDate),
    staff: (patient.assignedStaff ?? []).map((s) => ({ id: s.id, name: s.name })),
    phone: toPhoneInputValue(patient.phone),
    residence: patient.residence ?? '',
    residenceDetail: patient.residenceDetail ?? '',
    guardianName: patient.guardianName ?? '',
    guardianRelationship: patient.guardianRelationship ?? '',
    guardianPhone: toPhoneInputValue(patient.guardianPhone),
  };
}

const isGuardianMode = (patient: Patient) => patient.appUserType === 'guardian';

const trimmedRows = (rows: PatientMedication[]) =>
  rows.map((r) => ({ name: r.name.trim(), freq: r.freq.trim() }));

const sameRows = (a: PatientMedication[], b: PatientMedication[]) =>
  a.length === b.length && a.every((r, i) => r.name === b[i].name && r.freq === b[i].freq);

/**
 * The PUT body for this draft against the patient the edit session started from — changed fields only.
 * An empty object means there is nothing to save.
 *
 * Text is compared trimmed, phones as digits, so retyping a value that was already stored is not a change.
 * Guardian fields are only ever sent in 보호자 mode (the server answers GUARDIAN_FIELD_NOT_APPLICABLE
 * otherwise), and 담당 의료진 only when the detail response carried the list — without it there is no
 * baseline, and sending the draft's empty list would unassign everyone.
 */
export function buildUpdateInput(base: Patient, draft: PatientDraft): UpdatePatientInput {
  const input: UpdatePatientInput = {};

  const name = draft.name.trim();
  if (name !== (base.name ?? '').trim()) input.name = name;
  if (draft.gender && draft.gender !== (base.gender ?? '')) input.gender = draft.gender;
  if (draft.birthDate !== toDateInputValue(base.birthDate)) input.birthDate = draft.birthDate;

  const regNo = draft.regNo.trim();
  if (regNo !== (base.regNo ?? '').trim()) input.mrn = regNo;

  const dx = draft.diagnosis;
  if (
    dx &&
    dx.groupId != null &&
    (dx.groupName !== (base.mainDiagnosisName ?? null) || dx.code !== (base.diseaseCode ?? null))
  ) {
    input.mainDiagnosisGroupId = dx.groupId;
    // 부진단 removal travels on its own flag (SHCT-737 QC ②): an omitted code and a null code reach the
    // server identically, so the code field alone cannot say "remove it".
    if (dx.codeId == null) input.clearSubDiagnosis = true;
    else input.subDiagnosisCodeId = dx.codeId;
  }

  const kept = trimmedRows(draft.medication).filter((r) => r.name);
  if (!sameRows(kept, trimmedRows(medicationRows(base)))) {
    if (Array.isArray(base.medications)) {
      input.medications = kept;
    } else {
      // Legacy wire: one pair is all the server can store, so send exactly that.
      const only = kept[0] ?? { name: '', freq: '' };
      input.prescriptionDrug = only.name;
      input.medicationCycle = only.freq;
    }
  }

  if (draft.dischargeDate !== toDateInputValue(base.dischargeDate)) {
    input.dischargeDate = draft.dischargeDate;
  }

  if (Array.isArray(base.assignedStaff)) {
    const ids = draft.staff.map((s) => s.id);
    const stored = base.assignedStaff.map((s) => s.id);
    if (ids.length !== stored.length || ids.some((id, i) => id !== stored[i])) {
      input.assignedStaffIds = ids;
    }
  }

  const phone = toPhoneInputValue(draft.phone);
  if (phone !== (normalizePhone(base.phone) ?? '')) input.phone = phone;

  const residence = draft.residence.trim();
  if (residence !== (base.residence ?? '').trim()) input.residence = residence;
  const residenceDetail = draft.residenceDetail.trim();
  if (residenceDetail !== (base.residenceDetail ?? '').trim()) {
    input.residenceDetail = residenceDetail;
  }

  if (isGuardianMode(base)) {
    const guardianName = draft.guardianName.trim();
    if (guardianName !== (base.guardianName ?? '').trim()) input.guardianName = guardianName;
    if (
      draft.guardianRelationship &&
      draft.guardianRelationship !== (base.guardianRelationship ?? '')
    ) {
      input.guardianRelationship = draft.guardianRelationship;
    }
    const guardianPhone = toPhoneInputValue(draft.guardianPhone);
    if (guardianPhone !== (normalizePhone(base.guardianPhone) ?? '')) {
      input.guardianPhone = guardianPhone;
    }
  }

  return input;
}

const hasBadChars = (value: string) => hasEmoji(value) || /[\r\n]/.test(value);

/**
 * PRD 02 §4.2 for every CHANGED field at once (§3.6 조건 분기 「변경된 모든 필드를 한 번에 검증」).
 * Untouched fields are not re-judged: a legacy value that predates a rule must not block correcting
 * some other field. 처방약 follows the same rule for length and characters (checked only when the list is
 * being sent), with one exception: a row holding a 복약주기 with no 약품명 is always refused, because the
 * diff drops such a row and the operator's input would otherwise vanish without a word.
 *
 * Messages are the copy the registration modal already uses for the same fields (QA 기준 2026-09-20).
 */
export function validateDraft(base: Patient, draft: PatientDraft, t: Translate): DraftErrors {
  const input = buildUpdateInput(base, draft);
  const errors: DraftErrors = {};
  const reg = (key: string) => t(`patientManagement.registrationModal.errors.${key}`);
  const edit = (key: string, options?: Record<string, unknown>) =>
    t(`patientManagement.panel.edit.errors.${key}`, options);
  const guardianMode = isGuardianMode(base);
  const today = todayInKst();

  if (input.name !== undefined) {
    if (!input.name) errors.name = reg('name');
    else if (!PATIENT_NAME_REGEX.test(input.name)) errors.name = reg('nameLength');
  }
  if (input.birthDate !== undefined) {
    if (!input.birthDate) errors.birthDate = reg('birthDate');
    else if (input.birthDate > today) errors.birthDate = reg('birthDateFuture');
  }
  if (input.mrn !== undefined) {
    if (!input.mrn) errors.regNo = reg('regNo');
    else if (!MRN_REGEX.test(input.mrn)) errors.regNo = reg('regNoInvalid');
  }
  if (draft.diagnosis && draft.diagnosis.groupId == null) {
    errors.diagnosis = reg('primaryDiagnosis');
  }

  const rows = trimmedRows(draft.medication);
  const sendsMedication =
    input.medications !== undefined || input.prescriptionDrug !== undefined;
  if (
    sendsMedication &&
    rows.some((r) => r.name.length > MEDICATION_MAX || r.freq.length > MEDICATION_MAX)
  ) {
    errors.medication = edit('tooLong', { max: MEDICATION_MAX });
  } else if (sendsMedication && rows.some((r) => hasBadChars(r.name) || hasBadChars(r.freq))) {
    errors.medication = edit('invalidChars');
  } else if (rows.some((r) => !r.name && r.freq)) {
    errors.medication = edit('medicationNameRequired');
  }

  if (input.dischargeDate !== undefined) {
    if (!input.dischargeDate) errors.dischargeDate = reg('dischargeDate');
    else if (input.dischargeDate > today) errors.dischargeDate = reg('dischargeDateFuture');
  }
  if (input.assignedStaffIds !== undefined && input.assignedStaffIds.length === 0) {
    errors.staff = reg('staff');
  }

  // 환자 휴대폰 — 본인 모드는 필수, 보호자 대리 모드는 비워 저장할 수 있다(§4.2 · SHCT-981).
  const phone = toPhoneInputValue(draft.phone);
  const guardianPhone = toPhoneInputValue(draft.guardianPhone);
  if (input.phone !== undefined) {
    if (!guardianMode && !PHONE_REGEX.test(phone)) errors.phone = reg('phone');
    else if (guardianMode && phone && !PHONE_REGEX.test(phone)) {
      errors.phone = reg('phoneOptionalFormat');
    }
  }

  for (const [key, max] of [
    ['residence', RESIDENCE_MAX],
    ['residenceDetail', RESIDENCE_DETAIL_MAX],
  ] as const) {
    const value = input[key];
    if (value === undefined) continue;
    if (value.length > max) errors[key] = edit('tooLong', { max });
    else if (hasBadChars(value)) errors[key] = edit('invalidChars');
  }

  if (guardianMode) {
    if (input.guardianName !== undefined) {
      const name = input.guardianName;
      if (!name) errors.guardianName = reg('guardianName');
      else if (name.length < GUARDIAN_NAME_MIN || name.length > GUARDIAN_NAME_MAX) {
        errors.guardianName = reg('guardianNameLength');
      } else if (hasBadChars(name)) errors.guardianName = reg('guardianNameInvalid');
    }
    if (input.guardianPhone !== undefined && !PHONE_REGEX.test(guardianPhone)) {
      errors.guardianPhone = reg('guardianPhone');
    }
    // SHCT-536 — one number is never both the patient's and the guardian's. Reported on whichever of the
    // two the operator changed; the server checks the same rule against every other patient (409).
    if (phone && phone === guardianPhone) {
      if (input.phone !== undefined && !errors.phone) errors.phone = reg('phoneDuplicate');
      if (input.guardianPhone !== undefined && !errors.guardianPhone) {
        errors.guardianPhone = reg('phoneDuplicate');
      }
    }
  }

  return errors;
}
